import { z } from "zod";

// ---------------------------------------------------------------------------
// borrowing-power — a NEUTRAL borrowing-power + repayment-stress tool.
//
// The panel (panels/borrowing-power-estimator.md) made this binding: the value
// is NOT the most-precise capacity figure (that's a commodity every bank and
// comparison site ships, wired to their own policy HEM + the licensed HEM
// tables we cannot own). The value is making the INVISIBLE levers LEGIBLE:
//   1. the APRA 3.0pp serviceability buffer — WHY the bank's number is lower
//      than the "income × 5" heuristic;
//   2. the HEM living-expense FLOOR — a licensed, non-public benchmark we can
//      only APPROXIMATE, so it ships stamped-indicative and user-adjustable;
//   3. the APRA DTI≥6 → 20%-of-new-lending cap, IN FORCE from 1 Feb 2026 —
//      surfaced as portfolio-level LENDER RATIONING, NOT a hard per-borrower
//      ">6× = refused" cap.
//
// Regulatory posture (panel lens 2/3, BINDING): a home loan is NCCP credit, not
// an s764A financial product — the s765A health carve-out is irrelevant. A
// personalised borrowing-power result routed to a paid broker/lender/loan-
// product handoff is the C2-equivalent CREDIT-ASSISTANCE kill-line (NCCP
// "arranging" without an ACL, no carve-out). So `assertNoCreditHandoff` in
// calculate.ts runs over every personalised result and throws if any
// broker/lender/credit-product identifier leaks in. Affiliate is OUT OF SCOPE
// v1 — judged worth $0 on the personalised surface by mandate. Every output is
// an ESTIMATE lenders will assess differently — NOT a pre-approval.
//
// Pure functions only: inputs -> rules -> outputs. No I/O, no Date.now(); the
// assessment date / settings key is always passed in.
// ---------------------------------------------------------------------------

/** Loan repayment type — affects the notional repayment on the new loan. */
export const RepaymentTypeSchema = z.enum(["principal-and-interest", "interest-only"]);
export type RepaymentType = z.infer<typeof RepaymentTypeSchema>;

// ---------------------------------------------------------------------------
// borrowingPower — indicative max borrowable from income/expenses.
// ---------------------------------------------------------------------------

export const BorrowingPowerInputSchema = z.object({
  /**
   * Net (after-tax) annual income available to service a loan. The tool takes
   * net income directly — it does NOT re-derive tax here (that's the income-tax
   * engine's job on the surface that collects gross); the panel anchors the
   * moat on the buffer / amortisation / DTI layers, not a tax re-implementation.
   */
  netAnnualIncome: z.number().min(0),
  /**
   * Declared annual living expenses. The serviceability assessment uses the
   * HIGHER of this and the HEM floor (see `hemFloorAnnual`) — declaring less
   * than HEM does NOT lift capacity, because lenders floor expenses at HEM.
   */
  declaredAnnualExpenses: z.number().min(0).default(0),
  /**
   * HEM living-expense floor (annual) for this household. HEM is a LICENSED,
   * NON-PUBLIC benchmark (Melbourne Institute) — we can only APPROXIMATE it, so
   * this is supplied (and is user-adjustable on the surface) and the result is
   * stamped `hemIsEstimate: true`. When omitted, the ruleset's documented
   * approximation is used. Never presented as a precise figure.
   */
  hemFloorAnnual: z.number().min(0).optional(),
  /** Number of dependants — feeds the HEM approximation when no floor is given. */
  dependants: z.number().int().min(0).default(0),
  /** Whether the household is a couple — feeds the HEM approximation. */
  couple: z.boolean().default(false),
  /**
   * Existing annual debt commitments lenders count against serviceability —
   * other loan repayments, plus NOTIONAL repayments on credit-card LIMITS (not
   * balances). Pass the lender-style annualised figure; the convention is a
   * documented, adjustable assumption, not a canonical truth.
   */
  existingAnnualDebtCommitments: z.number().min(0).default(0),
  /**
   * Annual product (offer) interest rate as a fraction, e.g. 0.061 = 6.10%.
   * Serviceability is assessed at this PLUS the APRA buffer.
   */
  productRate: z.number().min(0).max(1),
  /** New-loan term in years for the notional repayment (typically 25 or 30). */
  loanTermYears: z.number().int().min(1).max(40).default(30),
  /** Repayment type for the notional new-loan repayment. */
  repaymentType: RepaymentTypeSchema.default("principal-and-interest"),
  /**
   * The APRA-settings key the assessment runs against (never derived from the
   * clock), e.g. "2026-02-01". Resolves the buffer + DTI cap in force then.
   */
  assessmentDate: z.string(),
});

export type BorrowingPowerInput = z.input<typeof BorrowingPowerInputSchema>;
export type BorrowingPowerParsed = z.infer<typeof BorrowingPowerInputSchema>;

/** How the DTI of the indicative result sits against the APRA ≥6 rationing band. */
export interface DtiFraming {
  /** Debt-to-income ratio = total debt (indicative loan + existing) / gross-equiv income proxy. */
  ratio: number;
  /** The APRA rationing threshold (6). */
  rationingThreshold: number;
  /** Portfolio cap: share of NEW lending ADIs may write at DTI ≥ threshold. */
  portfolioCapShareOfNewLending: number;
  /**
   * True when this result's DTI is at/above the rationing threshold. This is
   * NOT a refusal — it flags the result sits in the band lenders RATION at a
   * portfolio level (≤20% of new lending), so high-DTI applicants face tighter
   * availability. Copy must say "lenders ration", never "you cannot borrow".
   */
  inRationingBand: boolean;
}

export interface BorrowingPowerOutput {
  /** Net income the calc ran against. */
  netAnnualIncome: number;
  /** Living expenses applied = max(declared, HEM floor). */
  appliedAnnualExpenses: number;
  /** The HEM floor used (supplied or approximated). */
  hemFloorAnnual: number;
  /** True — the HEM floor is an INDICATIVE approximation, not a precise figure. */
  hemIsEstimate: boolean;
  /** Annual surplus available to service the new loan, after expenses + debts. */
  annualServiceableSurplus: number;
  /** The APRA serviceability buffer applied (percentage points, as a fraction). */
  bufferApplied: number;
  /** Assessment rate = product rate + buffer (the rate capacity is solved at). */
  assessmentRate: number;
  /**
   * Indicative maximum new loan principal — the closed-form amount whose notional
   * repayment at the ASSESSMENT rate consumes the serviceable surplus. An
   * ESTIMATE lenders will assess differently, NOT a pre-approval.
   */
  indicativeMaxLoan: number;
  /** DTI framing — APRA ≥6 portfolio rationing, NOT a hard per-borrower cap. */
  dti: DtiFraming;
}

// ---------------------------------------------------------------------------
// repaymentStress — the 3pp buffer reframed as personal headroom.
// ---------------------------------------------------------------------------

export const RepaymentStressInputSchema = z.object({
  /** Outstanding loan principal. */
  loanAmount: z.number().min(0),
  /** Current annual product interest rate as a fraction, e.g. 0.061 = 6.10%. */
  currentRate: z.number().min(0).max(1),
  /** Remaining term in years. */
  loanTermYears: z.number().int().min(1).max(40).default(30),
  /** Repayment type. */
  repaymentType: RepaymentTypeSchema.default("principal-and-interest"),
  /**
   * Offset/redraw balance that reduces the interest-bearing base (P&I and IO
   * both compute interest on principal − offset). Optional.
   */
  offsetBalance: z.number().min(0).default(0),
  /** APRA-settings key (never from the clock), e.g. "2026-02-01". */
  assessmentDate: z.string(),
});

export type RepaymentStressInput = z.input<typeof RepaymentStressInputSchema>;
export type RepaymentStressParsed = z.infer<typeof RepaymentStressInputSchema>;

/** One scenario in the stress strip: a rate delta and the resulting repayment. */
export interface StressScenario {
  /** Rate uplift over current (percentage points, as a fraction). 0 = current. */
  rateDelta: number;
  /** The annual rate this scenario assumes (current + delta). */
  rate: number;
  /** Monthly repayment under this scenario. */
  monthlyRepayment: number;
  /** Extra monthly repayment vs the current-rate baseline. */
  extraVsCurrentMonthly: number;
  /**
   * True for the APRA-buffer scenario (current + 3.0pp) — the rate the borrower
   * was assessed AGAINST. This is the headline "personal headroom" figure.
   */
  isApraBuffer?: boolean;
}

export interface RepaymentStressOutput {
  /** Loan + offset the calc ran against. */
  loanAmount: number;
  offsetBalance: number;
  /** Interest-bearing base = loanAmount − offsetBalance (floored at 0). */
  interestBearingBase: number;
  /** Monthly repayment at the current rate — the baseline. */
  currentMonthlyRepayment: number;
  /** The APRA buffer applied (percentage points, as a fraction). */
  bufferApplied: number;
  /** Monthly repayment at current + 3.0pp — the assessed-against rate. */
  bufferedMonthlyRepayment: number;
  /** Extra monthly cost the buffer represents (buffered − current). */
  bufferedExtraMonthly: number;
  /** The full scenario strip (+0.25 / +0.5 / +1.0pp and the +3.0pp buffer). */
  scenarios: readonly StressScenario[];
}

// ---------------------------------------------------------------------------
// Ruleset metadata — date-keyed (APRA macroprudential settings), NOT a per-
// product (au0x) FY ruleset, so it carries its own engine tag.
// ---------------------------------------------------------------------------

/** A documented HEM-approximation row — an INDICATIVE annual living-expense floor. */
export interface HemApproximationRow {
  /** Household: "single" or "couple". */
  household: "single" | "couple";
  /** Number of dependants this row applies to. */
  dependants: number;
  /**
   * Indicative annual HEM floor (AUD). APPROXIMATION ONLY — the real HEM tables
   * are licensed by the Melbourne Institute and not freely published. Period-
   * tagged; re-snapshot quarterly. Never present as a precise figure.
   */
  indicativeAnnualFloor: number;
}

export interface BorrowingPowerRuleSet {
  /** Engine tag — distinguishes this from a per-product (au0x) ruleset. */
  engine: "borrowing-power";
  /** Effective-from date of these APRA settings, e.g. "2026-02-01". */
  effectiveFrom: string;
  /** Effective-to date (null = current / open-ended). */
  effectiveTo: string | null;
  /** APRA serviceability buffer (percentage points, as a fraction). 0.03 = 3.0pp. */
  serviceabilityBuffer: number;
  /** APRA high-DTI rationing threshold (debt-to-income ratio). 6. */
  dtiRationingThreshold: number;
  /** Share of NEW lending ADIs may write at DTI ≥ threshold (portfolio cap). */
  dtiPortfolioCapShareOfNewLending: number;
  /**
   * INDICATIVE HEM approximation table — period-tagged, methodology-sourced to
   * the Melbourne Institute, VALUES APPROXIMATED (tables are licensed / non-
   * public). The serviceability surplus driven by this is an estimate, not a
   * quote. Exposed as user-adjustable on the surface.
   */
  hemApproximation: readonly HemApproximationRow[];
  /** Source-of-truth URLs the figures were transcribed from. */
  sources: Record<string, string>;
  /** Date the source pages were last verified, ISO. */
  sourcesVerifiedAt: string;
}
